import { PaymentMethodSummaryResponse } from '@monetize/types/app';
import { loadStripe } from '@stripe/stripe-js';
import { FunctionComponent as FC, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { apiPost, handleApiErrorToast } from '../../../api/axios';
import AppLoading from '../../../components/App/AppLoading';
import { ROUTES } from '../../../constants';
import { useQuery } from '../../../hooks';
import Sentry from '../../../services/sentry';
import { tempSharedQuotePaymentMethodState } from '../../../store/global.store';
import {
  getRecordShareCaptureContext,
  getRecordShareErrorLogContext,
} from '../../../utils/recordShareLogging';
import { processClientSecretId } from '../../../utils/stripe';

// This component handles the Stripe redirect callback when adding a payment method on Quote Share page
export const QuoteSharePaymentMethodStripeCallback: FC = () => {
  const params = useParams();
  const tenantId = params.tenantId || '';
  const recordId = params.recordId || '';
  const query = useQuery();
  const navigate = useNavigate();

  const [tempPaymentMethodData, setTempPaymentMethodData] = useAtom(
    tempSharedQuotePaymentMethodState,
  );

  const clientSecretId = query.get('setup_intent_client_secret');
  const token = query.get('token') || '';

  useEffect(() => {
    if (!tempPaymentMethodData || !clientSecretId) {
      return;
    }

    const run = async () => {
      try {
        const stripe = await loadStripe(tempPaymentMethodData.stripePublicKey);

        if (!stripe) {
          throw new Error('Sorry, failed to load stripe.');
        }

        const { setupIntent } = await processClientSecretId(
          stripe,
          clientSecretId,
        );

        const paymentToken = setupIntent.id;

        // Save Payment Method to backend
        await apiPost<PaymentMethodSummaryResponse>(
          `/api/share/quotes/${recordId}/paymentMethods`,
          {
            paymentMethod: {
              paymentToken,
              amount: 0,
            },
            paymentGatewayId: tempPaymentMethodData.paymentGatewayId,
          },
          {
            params: { token: tempPaymentMethodData.token },
            axiosConfig: {
              excludeUserIdFromHeader: true,
              excludeTenantIdFromHeader: true,
              customXTenantId: tenantId,
            },
          },
        );

        // Clear the temp state
        setTempPaymentMethodData(null);
      } catch (error) {
        // This runs after a Stripe redirect and then navigates away regardless,
        // so a failure here is otherwise invisible — the payment method is
        // silently lost and the user lands back on the quote without one.
        const logContext = getRecordShareErrorLogContext(
          { recordId, tenantId, token: tempPaymentMethodData.token },
          error,
        );
        Sentry.logger.error(
          'QuoteSharePage - stripe callback payment method save failed',
          logContext,
        );
        Sentry.captureException(
          error,
          getRecordShareCaptureContext(logContext, {
            type: 'QUOTE_SHARE',
            action: 'STRIPE_CALLBACK_SAVE_PAYMENT_METHOD',
          }),
        );
        handleApiErrorToast(error);
      }

      // Redirect back to the quote share page
      navigate(ROUTES.getQuoteShareRoute(tenantId, recordId, token, ''), {});
    };

    run();
  }, [
    tempPaymentMethodData,
    clientSecretId,
    navigate,
    tenantId,
    recordId,
    token,
    setTempPaymentMethodData,
  ]);

  return <AppLoading loadingText="Processing payment method..." />;
};
